/**
 * Bakes the stain volume. For each layer it compares that layer against its
 * neighbours (or the running background) and writes an RGBA stain slice, while
 * carrying a per-pixel running mean + decayed peak forward through time.
 */

import { Program, ScreenQuad, createTexture2D, type GLContext } from './gl';
import { CUSTOM_RULE_MARKER, shaders } from '../shaders';
import { ruleByKey } from './rules';
import type { RuleSettings } from './state';
import type { Volume } from './volume';

export class StainPass {
  private readonly gl: WebGL2RenderingContext;
  private program: Program | null = null;
  private currentSource = '';

  private stateTextures: [WebGLTexture, WebGLTexture] | null = null;
  private framebuffers: [WebGLFramebuffer, WebGLFramebuffer] | null = null;
  private stateIndex = 0;
  private width = 0;
  private height = 0;
  private stateFormat = 0;

  /** Layers already baked with the current settings, for incremental work. */
  private bakedCount = 0;

  constructor(
    ctx: GLContext,
    private readonly quad: ScreenQuad,
  ) {
    this.gl = ctx.gl;
    this.stateFormat = ctx.caps.colorBufferFloat ? this.gl.RGBA16F : this.gl.RGBA8;
  }

  /** Compiles the stain program with the given custom-rule body. Throws on shader errors. */
  compile(customSource: string): void {
    const body = customSource.trim().length ? customSource : 'return vec4(0.0);';
    const source = shaders.stainFrag.replace(CUSTOM_RULE_MARKER, body);
    if (source === this.currentSource && this.program) return;

    const next = new Program(this.gl, shaders.quadVert, source, 'stain');
    this.program?.dispose();
    this.program = next;
    this.currentSource = source;
    this.bakedCount = 0;
  }

  private ensureTargets(width: number, height: number): void {
    if (this.width === width && this.height === height && this.stateTextures && this.framebuffers) return;
    const { gl } = this;
    this.disposeTargets();

    // NEAREST throughout: the state is read with texelFetch and float linear
    // filtering is an optional extension we do not need.
    const a = createTexture2D(gl, { width, height, internalFormat: this.stateFormat, filter: gl.NEAREST });
    const b = createTexture2D(gl, { width, height, internalFormat: this.stateFormat, filter: gl.NEAREST });
    this.stateTextures = [a, b];

    const fboA = gl.createFramebuffer();
    const fboB = gl.createFramebuffer();
    if (!fboA || !fboB) throw new Error('createFramebuffer failed');
    this.framebuffers = [fboA, fboB];

    // Attachment 1 (the state target) is fixed per framebuffer; attachment 0 is
    // rebound to a different volume layer on every pass.
    gl.bindFramebuffer(gl.FRAMEBUFFER, fboA);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, a, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, fboB);
    gl.framebufferTexture2D(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT1, gl.TEXTURE_2D, b, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);

    this.width = width;
    this.height = height;
    this.stateIndex = 0;
    this.bakedCount = 0;
  }

  /** Drops any cached progress so the next bake starts from layer 0. */
  invalidate(): void {
    this.bakedCount = 0;
  }

  private applyUniforms(program: Program, volume: Volume, rule: RuleSettings): void {
    program
      .set('uDims', new Float32Array([volume.width, volume.height, volume.layers]))
      .set('uRing', volume.ringOffset)
      .set('uRule', ruleByKey(rule.key).id)
      .set('uCompare', rule.compare)
      .set('uStride', Math.max(1, Math.round(rule.stride)))
      .set('uBlur', Math.max(0, Math.min(3, Math.round(rule.blur))))
      .set('uColormap', rule.colormap)
      .set('uSourceColor', rule.sourceColor ? 1 : 0)
      .set('uGain', rule.gain)
      .set('uBias', rule.bias)
      .set('uThreshold', rule.threshold)
      .set('uSoftness', rule.softness)
      .set('uDecay', rule.decay)
      .set('uMeanRate', rule.meanRate)
      .set('uGamma', rule.gamma)
      .set('uParamA', rule.paramA)
      .set('uParamB', rule.paramB)
      .set('uParamC', rule.paramC);
  }

  /**
   * Bakes a contiguous run of logical layers. `resetState` restarts the running
   * mean/peak, which must happen whenever the run does not start at layer 0.
   */
  bakeRange(volume: Volume, rule: RuleSettings, from: number, to: number, resetState: boolean): void {
    if (!volume.videoTexture || !volume.stainTexture || volume.count === 0) return;
    if (!this.program) this.compile(rule.customSrc);
    const program = this.program;
    if (!program) return;

    const { gl } = this;
    this.ensureTargets(volume.width, volume.height);
    const states = this.stateTextures;
    const fbos = this.framebuffers;
    if (!states || !fbos) return;

    gl.disable(gl.BLEND);
    gl.viewport(0, 0, volume.width, volume.height);

    program.use();
    this.applyUniforms(program, volume, rule);
    program.texture('uVideo', 0, gl.TEXTURE_3D, volume.videoTexture);

    let reset = resetState;
    for (let layer = from; layer <= to; layer++) {
      const readIndex = this.stateIndex;
      const writeIndex = 1 - readIndex;

      gl.bindFramebuffer(gl.FRAMEBUFFER, fbos[writeIndex]);
      gl.framebufferTextureLayer(
        gl.FRAMEBUFFER,
        gl.COLOR_ATTACHMENT0,
        volume.stainTexture,
        0,
        volume.logicalToPhysical(layer),
      );
      gl.drawBuffers([gl.COLOR_ATTACHMENT0, gl.COLOR_ATTACHMENT1]);

      if (layer === from) {
        const status = gl.checkFramebufferStatus(gl.FRAMEBUFFER);
        if (status !== gl.FRAMEBUFFER_COMPLETE) {
          gl.bindFramebuffer(gl.FRAMEBUFFER, null);
          throw new Error(`Stain framebuffer incomplete (0x${status.toString(16)})`);
        }
      }

      program.set('uLayer', layer).set('uResetState', reset ? 1 : 0);
      program.texture('uStatePrev', 1, gl.TEXTURE_2D, states[readIndex]);
      this.quad.draw();

      this.stateIndex = writeIndex;
      reset = false;
    }

    gl.framebufferTextureLayer(gl.FRAMEBUFFER, gl.COLOR_ATTACHMENT0, null, 0, 0);
    gl.bindFramebuffer(gl.FRAMEBUFFER, null);
    gl.drawBuffers([gl.BACK]);
  }

  /** Bakes every filled layer from scratch. */
  bakeAll(volume: Volume, rule: RuleSettings): void {
    if (volume.count === 0) return;
    this.stateIndex = 0;
    this.bakeRange(volume, rule, 0, volume.count - 1, true);
    this.bakedCount = volume.count;
  }

  /** Bakes only layers appended since the last bake, continuing the running state. */
  bakeAppended(volume: Volume, rule: RuleSettings): void {
    if (volume.count === 0) return;
    if (this.bakedCount === 0) {
      this.bakeAll(volume, rule);
      return;
    }
    if (volume.count <= this.bakedCount) return;
    this.bakeRange(volume, rule, this.bakedCount, volume.count - 1, false);
    this.bakedCount = volume.count;
  }

  /** Bakes a single layer, continuing the running state. Used by the live ring. */
  bakeLayer(volume: Volume, rule: RuleSettings, layer: number, resetState = false): void {
    this.bakeRange(volume, rule, layer, layer, resetState);
    this.bakedCount = Math.max(this.bakedCount, Math.min(volume.count, layer + 1));
  }

  private disposeTargets(): void {
    const { gl } = this;
    if (this.stateTextures) {
      gl.deleteTexture(this.stateTextures[0]);
      gl.deleteTexture(this.stateTextures[1]);
      this.stateTextures = null;
    }
    if (this.framebuffers) {
      gl.deleteFramebuffer(this.framebuffers[0]);
      gl.deleteFramebuffer(this.framebuffers[1]);
      this.framebuffers = null;
    }
    this.width = 0;
    this.height = 0;
  }

  dispose(): void {
    this.disposeTargets();
    this.program?.dispose();
    this.program = null;
    this.currentSource = '';
  }
}
